import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { AirspaceCheckerParametersWithStartEnd } from '@flyfreely-portal-ui/airspace';
import {
    AirspaceJurisdictionDto,
    FlyFreelyLoggingService,
    ImageReference,
    LocationDetailsDto,
    LocationDto,
    LocationFeatureDto,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { FullScreenService } from '@flyfreely-portal-ui/fullscreen';
import {
    FeatureAndGroup,
    FeatureGroup,
    MapDataInput,
    MapFeature,
    MapViewport
} from '@flyfreely-portal-ui/map';
import booleanValid from '@turf/boolean-valid';
import { getOrElse, map } from 'fp-ts/es6/Option';
import { pipe } from 'fp-ts/es6/function';
import { GeoJsonProperties, GeometryCollection, LineString } from 'geojson';
import { buildAreaLineFeature } from 'libs/map/src/lib/draw-modes/draw-area-line';
import { FlyFreelyMapComponent } from 'libs/map/src/lib/flyfreely-map/flyfreely-map.component';
import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { flightAreaOfFeatures } from '../helpers';
import { MissionAirspaceParameters } from '../location-edit-v2/location-edit-dialogue.component';

// Mapbox requires a non-negative value for IDs
const NEW_FEATURE = 0;

@Component({
    selector: 'location-edit-drawing',
    templateUrl: './location-edit-drawing.component.html',
    styleUrls: ['./location-edit-drawing.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocationEditDrawingComponent
    implements OnInit, OnChanges, OnDestroy
{
    /** The feature groups to display and edit. The component manages an internal
     * mutable copy; updates are emitted via featureGroupsChange. */
    @Input() featureGroups: FeatureGroup[];

    @Input() organisationId: number;
    @Input() showAirspaceChecker: boolean = false;
    @Input() type: LocationDetailsDto.Type | LocationDto.Type =
        LocationDto.Type.TEMPLATE;
    @Input() missionValues: MissionAirspaceParameters;
    @Input() workTracker: WorkTracker;
    /** Observable of the current airspace checker parameters */
    @Input() airspaceValue$: Observable<AirspaceCheckerParametersWithStartEnd>;
    /** Observable of external overlay data (LOTAM, MISSIONS, etc.) */
    @Input() mapData$: Observable<MapDataInput>;
    @Input() currentTimeZone: string;
    @Input() additionalImages?: ImageReference[];
    /**
     * The minimum feature types required for the location to be valid.
     * If any of the types in the array are present, the location will be valid.
     * If an empty array is passed in, any feature will be valid.
     * Defaults to the minimum required types for location templates and mission locations.
     */
    @Input() requiredFeatureTypes: string[] = [
        LocationFeatureDto.Type.FLIGHT_AREA,
        LocationFeatureDto.Type.OFFSET_FLIGHT_AREA
    ];
    @Input() mapJurisdiction: AirspaceJurisdictionDto;
    /** Controlled by parent (after checking preferences); shows the airspace disclaimer overlay */
    @Input() showAirspaceDisclaimer = false;

    // ---- Outputs that carry updated state back to the parent ----

    /** Emits full updated featureGroups array after any edit */
    @Output() featureGroupsChange = new EventEmitter<FeatureGroup[]>();
    @Output() hasRequiredFeaturesChange = new EventEmitter<boolean>();
    @Output() hasAnyFeaturesChange = new EventEmitter<boolean>();
    @Output() flightAreaChange = new EventEmitter<GeoJSON.Polygon | null>();

    // ---- Outputs that proxy map events the parent needs to react to ----

    @Output() geocodeResultSelected = new EventEmitter<MapFeature>();
    @Output() viewportChange = new EventEmitter<MapViewport>();
    @Output() jurisdictionChanged = new EventEmitter<AirspaceJurisdictionDto>();
    @Output() airspaceValuesUpdated =
        new EventEmitter<AirspaceCheckerParametersWithStartEnd>();
    @Output() airspaceDisclaimerAcknowledged = new EventEmitter<void>();
    @Output() editModeChanged = new EventEmitter<boolean | string>();
    @Output() isAddingFeatureChange = new EventEmitter<boolean>();
    @Output() isEditingChange = new EventEmitter<boolean>();

    // ---- ViewChildren ----

    @ViewChild('map', { static: false }) map: FlyFreelyMapComponent;
    @ViewChild('drawingArea', { static: true }) drawingArea: ElementRef;

    // ---- Internal state ----

    /** Internal mutable copy of featureGroups */
    internalFeatureGroups: FeatureGroup[] = [];

    mapReady = false;
    private isLoaded = false;

    hoverFeatureId: number;
    editFeatureId: number;

    isAddingFeature = false;
    isEditing = false;

    selectedFeature: FeatureAndGroup;
    addedFeature: FeatureAndGroup;
    selectedFeatureGroup: FeatureGroup;

    invalidFeatures: Set<string> = new Set<string>();

    /** Tracked internally for passing to the airspace checker sidebar */
    flightArea: GeoJSON.Polygon | null = null;

    private nextId = 1;
    private featuresUpdated$ = new Subject<boolean>();
    private ngUnsubscribe$ = new Subject<void>();
    /** Reference guard: tracks the last array emitted via featureGroupsChange so we
     * can ignore the echo when the parent passes it back through the [featureGroups] binding. */
    private _lastEmittedFeatureGroups: FeatureGroup[] | null = null;

    constructor(
        private fullscreenService: FullScreenService,
        private logging: FlyFreelyLoggingService,
        private changeDetector: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.featuresUpdated$
            .pipe(debounceTime(300), takeUntil(this.ngUnsubscribe$))
            .subscribe(updated => {
                if (!this.isLoaded) {
                    this.isLoaded = this.map.zoomToAllFeatures(updated);
                } else {
                    this.map.zoomToAllFeatures(updated);
                }
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['featureGroups']) {
            const incoming = this.featureGroups ?? [];
            // Skip the echo: when the parent stores our emitted array and passes it
            // back via the binding, the reference will be identical — ignore it.
            if (incoming === this._lastEmittedFeatureGroups) {
                return;
            }
            // Sync the internal copy whenever the parent provides a genuinely new set
            // (initial load, KML upload reset, etc.)
            this.internalFeatureGroups = incoming;
            this.validateFeatures();
            this.refreshValidity();
            this.updateNextId();

            // If the map is already ready, zoom to show the newly loaded features
            if (!changes['featureGroups'].firstChange && this.mapReady) {
                this.featuresUpdated$.next(false);
            }
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.featuresUpdated$.complete();
    }

    // ---- Keyboard ----

    @HostListener('document:keyup', ['$event'])
    handleDeleteKeyboardEvent(event: KeyboardEvent) {
        if (
            event.key === 'Delete' &&
            this.selectedFeatureGroup &&
            this.selectedFeature
        ) {
            this.deleteFeature(
                this.selectedFeatureGroup,
                this.selectedFeature.feature
            );
        }
    }

    // ---- Public API for parent (accessed via @ViewChild) ----

    /** Call before closing the modal to cancel any in-progress edits. */
    handleEditOnClose() {
        if (this.isAddingFeature && this.selectedFeature != null) {
            this.cancelAddingFeature();
            this.map.doneEditing();
            this.onFeatureDelete(this.selectedFeature);
            this._setIsAddingFeature(false);
        }
        if (this.isEditing) {
            this.map.doneEditing();
        }
    }

    /** Returns the current internal feature groups (for use at save time). */
    getFeatureGroups(): FeatureGroup[] {
        return this.internalFeatureGroups;
    }

    /** Finish active map edit (called by parent before performing save). */
    doneEditing() {
        this.map.doneEditing();
    }

    /** Trigger zoom to all features (delegates to map). */
    zoomToAllFeatures(update?: boolean) {
        this.featuresUpdated$.next(update ?? false);
    }

    // ---- Map event handlers ----

    onMapReady() {
        if (!this.mapReady) {
            this.mapReady = true;
            this.featuresUpdated$.next(true);
        }
    }

    onFeaturesLoaded() {
        if (!this.isLoaded) {
            this.featuresUpdated$.next(false);
        }
    }

    onFeaturesUpdated(updated: FeatureAndGroup) {
        if (this.isAddingFeature) {
            return;
        }

        const isNew = updated.feature.id == null;

        const feature = !isNew
            ? updated
            : {
                  groupId: updated.groupId,
                  feature: {
                      ...updated.feature,
                      id: NEW_FEATURE
                  }
              };

        if (feature?.feature.children != null) {
            feature.feature.children.forEach(c => {
                c.categoryId = feature.feature.categoryId;
            });
        }

        const updateFeature = (existing: MapFeature[], toAdd: MapFeature) =>
            !isNew
                ? existing.map(f => (f.id === toAdd.id ? toAdd : f))
                : existing.concat(toAdd);

        this.internalFeatureGroups = this.internalFeatureGroups.map(g =>
            g.id === updated.groupId
                ? {
                      ...g,
                      existingFeatures: updateFeature(
                          g.existingFeatures,
                          feature.feature
                      )
                  }
                : g
        );

        this.refreshValidity();

        if (feature.feature.id === NEW_FEATURE) {
            this.map.showFeature(feature);
        }

        this._lastEmittedFeatureGroups = this.internalFeatureGroups;
        this.featureGroupsChange.emit(this.internalFeatureGroups);
    }

    onFeatureSelected(selectedFeature: FeatureAndGroup) {
        if (this.isAddingFeature) {
            return;
        }

        if (
            this.selectedFeature != null &&
            this.selectedFeature.feature.id === selectedFeature.feature.id
        ) {
            this.onFeatureEdit(selectedFeature);
        } else {
            this.setSelectedFeature(selectedFeature);
        }
    }

    onFeatureUnselected() {
        if (!this.isAddingFeature) {
            this.selectedFeature = null;
            this.selectedFeatureGroup = null;
        }
    }

    onFeatureEdit(feature: FeatureAndGroup) {
        this.map.editFeature(
            {
                feature: feature.feature,
                groupId: feature.groupId
            },
            this.findDrawProperties(feature)
        );
        this.setSelectedFeature(feature);
    }

    onFeatureDelete(feature: FeatureAndGroup) {
        const featureGroup = this.internalFeatureGroups.find(
            fg => fg.id === feature.groupId
        );
        this.deleteFeature(featureGroup, feature.feature);
        this.setSelectedFeature(null);
        this.refreshValidity();
        this.map.hidePopup();
    }

    onSelectedFeatureUpdated(updated: FeatureAndGroup) {
        window.setTimeout(() => {
            if (
                updated.feature.categoryId ===
                    LocationFeatureDto.Type.OFFSET_FLIGHT_AREA ||
                updated.feature.categoryId ===
                    LocationFeatureDto.Type.OFFSET_NO_FLY_AREA
            ) {
                this.handleOffsetFeatureChanged(updated);
            }

            this.setFeatureName(updated);
            this.setSelectedFeature(updated);
            this.onFeaturesUpdated(updated);
            this.changeDetector.detectChanges();
        });
    }

    onFeatureHover(featureId: number) {
        this.hoverFeatureId = featureId;
    }

    onFeatureNameSelected(feature: MapFeature) {
        if (this.showAirspaceDisclaimer) {
            return;
        }
        const groupId = this.internalFeatureGroups.find(
            g => g.existingFeatures.filter(f => f.id === feature.id).length > 0
        ).id;
        const featureAndGroup: FeatureAndGroup = {
            groupId,
            feature
        };
        this.setSelectedFeature(featureAndGroup);
        this.map.zoomToFeature(feature, true, false);
    }

    onGeocodeResultSelected(feature: MapFeature) {
        this.geocodeResultSelected.emit(feature);
    }

    onMapJurisdictionChanged(jurisdiction: AirspaceJurisdictionDto) {
        this.jurisdictionChanged.emit(jurisdiction);
    }

    onAirspaceValuesUpdated(values: AirspaceCheckerParametersWithStartEnd) {
        this.airspaceValuesUpdated.emit(values);
    }

    onAirspaceDisclaimerAcknowledged() {
        this.airspaceDisclaimerAcknowledged.emit();
    }

    onFullscreenRequested() {
        this.fullscreenService.toggleFullScreen(this.drawingArea);
    }

    onEditModeChanged(isEditingOrEvent: boolean | string) {
        if (isEditingOrEvent === 'cancelled') {
            this.handlePolygonDrawingCancelled();
            return;
        }

        if (this.isEditing && this.isAddingFeature && !isEditingOrEvent) {
            this._setIsAddingFeature(false);
            this.refreshValidity();
            this.map.showPopup();
            this.setSelectedFeature(this.addedFeature);
        }

        if (!isEditingOrEvent) {
            this.validateFeatures();
        }

        this._setIsEditing(isEditingOrEvent as boolean);
        this.editModeChanged.emit(isEditingOrEvent);
        this.changeDetector.detectChanges();
    }

    handlePolygonDrawingCancelled() {
        if (this.isAddingFeature && this.addedFeature) {
            this.cancelAddingFeature();
            this._setIsAddingFeature(false);
            this.logging.info(
                'Polygon drawing cancelled - at least 3 points are required to create a polygon'
            );
            this.setSelectedFeature(null);
            this._setIsEditing(false);
            this.changeDetector.detectChanges();
        }
    }

    onViewportChange(viewport: MapViewport) {
        this.viewportChange.emit(viewport);
    }

    // ---- Feature management ----

    addNewFeature(featureGroup: FeatureGroup) {
        this.cancelAddingFeature();

        this._setIsAddingFeature(true);
        const newFeature: MapFeature = {
            id: this.nextId++,
            name: this.isExistingFeaturesValid(featureGroup)
                ? this.numerateNameIfExist(
                      featureGroup.categories[0].name,
                      featureGroup.existingFeatures
                  )
                : featureGroup.categories[0].name,
            categoryId: featureGroup.categories[0].id,
            geom: {
                // @ts-ignore - bad type sensing
                type: featureGroup.type
            }
        };
        this.selectedFeature = {
            groupId: featureGroup.id,
            feature: newFeature
        };
        this.addedFeature = this.selectedFeature;

        this.internalFeatureGroups = this.internalFeatureGroups.map(g =>
            g.id === featureGroup.id
                ? {
                      ...g,
                      existingFeatures: g.existingFeatures.concat(newFeature)
                  }
                : g
        );

        this.map.editFeature({
            groupId: featureGroup.id,
            feature: newFeature
        });
    }

    cancelAddingFeature() {
        if (this.isAddingFeature && this.selectedFeature != null) {
            this.internalFeatureGroups = this.internalFeatureGroups.map(g =>
                g.id === this.selectedFeature.groupId
                    ? {
                          ...g,
                          existingFeatures: g.existingFeatures.filter(
                              f => f.id !== this.selectedFeature.feature.id
                          )
                      }
                    : g
            );
        }
        this.validateFeatures();
    }

    editFeature(featureGroup: FeatureGroup, feature: MapFeature) {
        this.editFeatureId = <number>feature.id;
        this.map.editFeature({
            feature,
            groupId: featureGroup.id
        });
    }

    deleteFeature(featureGroup: FeatureGroup, feature: MapFeature) {
        this.map.doneEditing();

        this.internalFeatureGroups = this.internalFeatureGroups.map(fg =>
            fg.id !== featureGroup.id
                ? fg
                : {
                      ...fg,
                      existingFeatures: fg.existingFeatures.filter(
                          f => f.id !== feature.id
                      )
                  }
        );
        this.validateFeatures();
        this._lastEmittedFeatureGroups = this.internalFeatureGroups;
        this.featureGroupsChange.emit(this.internalFeatureGroups);
    }

    highlightFeature(featureGroup: FeatureGroup, highlightFeatureId: number) {
        this.map.highlightFeature(featureGroup.id, highlightFeatureId);
    }

    unhighlightFeature(featureGroup: FeatureGroup, highlightFeatureId: number) {
        this.map.unhighlightFeature(featureGroup.id, highlightFeatureId);
    }

    // ---- Validity ----

    validateFeatures() {
        this.invalidFeatures.clear();
        this.internalFeatureGroups.forEach(group => {
            if (group.name === 'Corridors') {
                return;
            }
            group.existingFeatures.forEach(feature => {
                const featureKey = `${group.id}-${feature.id}`;
                if (!this.isFeatureValid(feature)) {
                    this.invalidFeatures.add(featureKey);
                }
            });
        });
        this.changeDetector.detectChanges();
    }

    private isFeatureValid(feature: MapFeature): boolean {
        try {
            if (!feature.geom) {
                return false;
            }
            const geoJsonFeature = {
                type: 'Feature' as const,
                geometry: feature.geom,
                properties: feature.properties || {}
            };
            return booleanValid(geoJsonFeature);
        } catch (error) {
            return false;
        }
    }

    isFeatureInvalid(groupId: number, featureId: number | string): boolean {
        return this.invalidFeatures.has(`${groupId}-${featureId}`);
    }

    hasInvalidFeatures(): boolean {
        return this.invalidFeatures.size > 0;
    }

    private refreshValidity() {
        const mergeAcc = (
            a: { hasAnyFeatures: boolean; hasRequiredFeatures: boolean },
            b: { hasAnyFeatures: boolean; hasRequiredFeatures: boolean }
        ) => ({
            hasAnyFeatures: a.hasAnyFeatures || b.hasAnyFeatures,
            hasRequiredFeatures: a.hasRequiredFeatures || b.hasRequiredFeatures
        });

        const { hasAnyFeatures, hasRequiredFeatures } =
            this.internalFeatureGroups.reduce(
                (acc, fg) =>
                    mergeAcc(
                        acc,
                        fg.existingFeatures.reduce(
                            (acc2, f) => ({
                                hasAnyFeatures: true,
                                hasRequiredFeatures:
                                    acc2.hasRequiredFeatures ||
                                    this.requiredFeatureTypes.includes(
                                        f.categoryId
                                    ) ||
                                    this.requiredFeatureTypes.length === 0
                            }),
                            {
                                hasAnyFeatures: false,
                                hasRequiredFeatures: false
                            } as {
                                hasAnyFeatures: boolean;
                                hasRequiredFeatures: boolean;
                            }
                        )
                    ),
                { hasAnyFeatures: false, hasRequiredFeatures: false }
            );

        this.hasRequiredFeaturesChange.emit(hasRequiredFeatures);
        this.hasAnyFeaturesChange.emit(hasAnyFeatures);

        const flightArea = pipe(
            flightAreaOfFeatures(this.getLocationFeatures()),
            map<any, GeoJSON.Polygon>(f =>
                f.geometry.type !== 'GeometryCollection'
                    ? <GeoJSON.Polygon>f.geometry
                    : <GeoJSON.Polygon>(
                          (<GeometryCollection>f.geometry).geometries.find(
                              g => g.type === 'Polygon'
                          )
                      )
            ),
            getOrElse(() => null as GeoJSON.Polygon)
        );

        this.flightArea = flightArea;
        this.flightAreaChange.emit(flightArea);
    }

    /** Extract location-feature-like objects from internal feature groups for helpers. */
    private getLocationFeatures(): any[] {
        return this.internalFeatureGroups.reduce(
            (acc: any[], fg) =>
                acc.concat(
                    fg.existingFeatures.map(f => ({
                        name: f.name,
                        geometry: f.geom,
                        type: f.categoryId,
                        properties: f.properties
                    }))
                ),
            []
        );
    }

    // ---- Private helpers ----

    private setSelectedFeature(feature: FeatureAndGroup) {
        this.selectedFeature = feature;
        this.selectedFeatureGroup = feature
            ? this.internalFeatureGroups.find(fg => fg.id === feature.groupId)
            : null;
    }

    private setFeatureName(feature: FeatureAndGroup) {
        if (!!feature && this.isSelectedFeatureGroupValid()) {
            const categories = this.selectedFeatureGroup.categories;
            const presetCategoryNames = categories.map(c => c.name);
            const isFeatureNameDefault =
                presetCategoryNames.includes(feature.feature.name) ||
                presetCategoryNames.some(presetCategoryName =>
                    new RegExp(`^${presetCategoryName}( \\d+)?$`).test(
                        feature.feature.name
                    )
                );
            const featureGroup = this.internalFeatureGroups.find(
                fg => fg.id === feature.groupId
            );
            if (isFeatureNameDefault) {
                const newName = categories.find(
                    category => category.id === feature.feature.categoryId
                ).name;
                feature.feature.name =
                    this.isExistingFeaturesValid(featureGroup) &&
                    featureGroup.existingFeatures.filter(
                        ef => ef.id !== feature.feature.id
                    ).length > 0
                        ? this.numerateNameIfExist(
                              newName,
                              featureGroup.existingFeatures.filter(
                                  ef => ef.id !== feature.feature.id
                              )
                          )
                        : newName;
            }
        }
    }

    isSelectedFeatureGroupValid() {
        return (
            !!this.selectedFeatureGroup &&
            !!this.selectedFeatureGroup.categories &&
            Array.isArray(this.selectedFeatureGroup.categories) &&
            this.selectedFeatureGroup.categories.length !== 0
        );
    }

    numerateNameIfExist(name: string, existingFeatures: MapFeature[]) {
        const existNames = existingFeatures.map(e => e.name);
        const baseNamePattern = new RegExp(`^${name}( \\d+)?$`);

        let matchingNames = existNames.filter(e => baseNamePattern.test(e));
        if (matchingNames.length > 0) {
            if (matchingNames.length === 1 && matchingNames[0] === name) {
                const newDefault = `${name} 1`;
                existingFeatures[0].name = newDefault;
                matchingNames = [newDefault];
            }
            const numbers = matchingNames.map(n => {
                const match = n.match(/\d+$/);
                return match ? parseInt(match[0], 10) : 0;
            });
            const maxNumber = Math.max(...numbers);
            return `${name} ${maxNumber + 1}`;
        }
        return name;
    }

    isExistingFeaturesValid(featureGroup: FeatureGroup | null) {
        return (
            featureGroup != null && featureGroup.existingFeatures.length !== 0
        );
    }

    private findDrawProperties(feature: FeatureAndGroup): GeoJsonProperties {
        const group = this.internalFeatureGroups.find(
            g => g.id === feature.groupId
        );
        if (group == null) {
            return undefined;
        }

        if (group.type !== 'Point' || feature.feature.categoryId == null) {
            return undefined;
        }

        const style = group.styles.symbol.find(
            s =>
                s.layout?.['icon-image'] != null &&
                s.filter[0] === '==' &&
                s.filter[2] === feature.feature.categoryId
        );

        return style.layout;
    }

    private handleOffsetFeatureChanged(featureAndGroup: FeatureAndGroup) {
        const linestring = (<GeometryCollection>(
            featureAndGroup.feature.geom
        )).geometries.find(g => g.type === 'LineString');
        const updatedFeature = buildAreaLineFeature(
            <LineString>linestring,
            featureAndGroup.feature.properties
        );
        featureAndGroup.feature.geom = updatedFeature.geometry;
        featureAndGroup.feature.properties = updatedFeature.properties;
    }

    /** Increment nextId for existing features in the groups, so that any new features have unique IDs */
    private updateNextId() {
        this.nextId = 1;

        this.internalFeatureGroups.forEach(group => {
            group.existingFeatures.forEach(feature => this.nextId++);
        });
    }

    private _setIsAddingFeature(value: boolean) {
        this.isAddingFeature = value;
        this.isAddingFeatureChange.emit(value);
    }

    private _setIsEditing(value: boolean) {
        this.isEditing = value;
        this.isEditingChange.emit(value);
    }
}
